/**
 * CommentList — displays existing comments and (if `canWrite`) an inline
 * composer. Delete affordance is limited to the author of each comment,
 * admins, and super admins (server enforces).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { FileImage } from "@/components/ui/file-image";
import { Trash } from "@phosphor-icons/react";
import { listComments, addComment, deleteComment } from "@/lib/publishApi";
import { formatApiError } from "@/lib/api";

function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h`;
  return new Date(iso).toLocaleDateString();
}

export function CommentList({
  postId, canWrite, currentUserId, onCommentsChanged,
  initialComments = null, readOnly = false,
}) {
  const [comments, setComments] = useState(initialComments || []);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { items } = await listComments(postId);
      setComments(items || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    // Skip authed fetch when caller hydrated us (anonymous public detail path).
    if (!readOnly) { load(); }
  }, [postId, readOnly]);

  const submit = async () => {
    const v = text.trim();
    if (!v) return;
    setBusy(true);
    try {
      const c = await addComment(postId, v);
      setComments(prev => [...prev, c]);
      setText("");
      onCommentsChanged?.(comments.length + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await deleteComment(id);
      setComments(prev => prev.filter(c => c.id !== id));
      onCommentsChanged?.(comments.length - 1);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="space-y-3" data-testid={`comments-${postId}`}>
      <h4 className="text-sm font-semibold">Comments ({comments.length})</h4>

      {canWrite && (
        <div className="border border-border rounded-md p-2 space-y-2">
          <Textarea
            value={text} onChange={e => setText(e.target.value)}
            placeholder="Write a comment…"
            rows={2}
            className="border-0 focus-visible:ring-0 resize-none p-1"
            data-testid="comment-input"
          />
          <div className="flex justify-end">
            <Button size="sm" onClick={submit} disabled={busy || !text.trim()}
                    data-testid="comment-submit-btn">
              {busy ? "Posting…" : "Post"}
            </Button>
          </div>
        </div>
      )}

      {comments.length === 0 ? (
        <p className="text-xs text-muted-foreground">Be the first to comment.</p>
      ) : (
        <ul className="space-y-3">
          {comments.map(c => (
            <li key={c.id} className="flex gap-2" data-testid={`comment-${c.id}`}>
              <div className="w-8 h-8 shrink-0 rounded-full bg-muted overflow-hidden flex items-center justify-center border border-border">
                {c.author_photo_file_id ? (
                  <FileImage fileId={c.author_photo_file_id} alt="" className="w-full h-full object-cover"
                             fallback={<span className="text-xs font-semibold">{(c.author_name || "?")[0]}</span>} />
                ) : (
                  <span className="text-xs font-semibold text-muted-foreground">
                    {(c.author_name || "?")[0].toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-semibold">{c.author_name}</span>
                  <span className="text-[11px] text-muted-foreground">{timeAgo(c.created_at)}</span>
                  {c.author_user_id === currentUserId && (
                    <button onClick={() => remove(c.id)}
                            className="ml-auto text-muted-foreground hover:text-rose-600"
                            data-testid={`comment-delete-${c.id}`}>
                      <Trash size={12} />
                    </button>
                  )}
                </div>
                <p className="text-sm whitespace-pre-wrap break-words">{c.body}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
